import {type ShareDataResponse, ShareService} from "./shareService";
import {autoRegister, resolve} from "../../container";
import {Interval} from "../../common/interval";
import type {Nullable} from "../../bootstrap/common/types";
import {EopErrorHandler} from "../../common/errorHandler";

export const SHARE_DATA_UPDATE_INTERVAL = 600000; // 10 minutes

export type ShareData = {
    value: number;
    time: number;
    change: number;
    changePercent: number;
};

export interface ShareDataObserver {
    receiveData: (data: Nullable<ShareData>) => void;
}

export interface ShareDataSource {
    register: (observer: ShareDataObserver) => void;
}

@autoRegister()
export class BufferingShareDataSource implements ShareDataSource {
    private observers: ShareDataObserver[] = [];
    private latestReceivedData: Nullable<ShareData> = null;

    public constructor(
        private shareService: ShareService = resolve(ShareService),
        private interval: Interval = resolve(Interval),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        this.fetch();
        void this.interval.repeat(() => this.fetch(), SHARE_DATA_UPDATE_INTERVAL);
    }

    public register(observer: ShareDataObserver): void {
        this.observers.push(observer);
        observer.receiveData(this.latestReceivedData);
    }

    private fetch(): void {
        this.shareService.fetch()
            .then(response => {
                const data = this.mapResponse(response);
                this.update(data);
            })
            .catch(error => {
                this.errorHandler.report(error);
                this.update(null);
            });
    }

    private update(data: Nullable<ShareData>): void {
        this.observers.forEach(observer => observer.receiveData(data));
        this.latestReceivedData = data;
    }

    private mapResponse(response: ShareDataResponse): ShareData {
        return {
            value: response.LAST,
            time: response.DATETIME_LAST_R,
            change: response.PREVIOUSDAYDIFFVALUE,
            changePercent: response.PREVIOUSDAYDIFFPERCENT
        };
    }
}
